"use client"
import { useState, useEffect } from "react"
import { LINKS } from "../../constants/links"
import Button from "../Button/Button"
import styles from "./Header.module.css"
import Link from "next/link"

// Define types for navigation structure
interface LinkType {
  name: string
  path: string
  children?: LinkType[]
  [key: string]: unknown // For any extra properties
}

// Type guard for LinkType with children
function hasChildren(
  link: unknown
): link is LinkType & { children: LinkType[] } {
  return (
    typeof link === "object" &&
    link !== null &&
    "children" in link &&
    Array.isArray((link as { children?: unknown }).children)
  )
}

// --- NEW: ServicesGrid component to avoid code duplication ---
interface ServicesGridProps {
  services: LinkType[]
  mainLinks?: [string, LinkType][]
  fadeUp?: boolean
  isMenuOpen?: boolean
  isNavHovered?: boolean
  showMainLinksColumn?: boolean
}
function ServicesGrid({
  services,
  mainLinks = [],
  fadeUp = false,
  isMenuOpen = false,
  isNavHovered = false,
  showMainLinksColumn = false,
}: ServicesGridProps) {
  const mainServices = services.slice(0, services.length - 4)
  const lastMainWithLastThree = services.slice(services.length - 4)
  const duration = 1000 // ms

  return (
    <div className="grid xl:grid-cols-5 gap-10 border border-t-slate-200 w-full bg-white text-[var(--color-dark)] justify-between py-12 px-6 xl:px-16 text-[16px] 3xl:px-24 2  3xl:text-[38px] uppercase">
      {/* Optionally render main links column (for menu dropdown) */}
      {showMainLinksColumn && (
        <div
          className={`flex flex-col transition-all duration-[${duration}ms] ${
            fadeUp
              ? isMenuOpen
                ? "opacity-100 translate-y-0"
                : "opacity-0 translate-y-8"
              : ""
          }`}
        >
          {mainLinks.map(([, value]) => (
            <a
              key={value.path}
              href={value.path}
              className={`font-medium font-oswald mb-6 text-[var(--color-dark)] transition-all duration-[${duration}ms] ${
                fadeUp
                  ? isMenuOpen
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                  : ""
              }`}
            >
              {value.name}
            </a>
          ))}
        </div>
      )}
      {/* Main services columns */}
      {mainServices.map((main) => {
        return (
          <div
            className={`flex flex-col transition-all duration-[${duration}ms] ${
              fadeUp
                ? isMenuOpen || isNavHovered
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
                : ""
            }`}
            key={main.path}
          >
            <a
              href={main.path}
              className={`font-medium font-oswald mb-6 text-[var(--color-dark)] transition-all duration-[${duration}ms] ${
                fadeUp
                  ? isMenuOpen || isNavHovered
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                  : ""
              }`}
            >
              {main.name}
            </a>
            {main.children && (
              <ul>
                {main.children.map((sub) => (
                  <li key={sub.path}>
                    <a
                      href={sub.path}
                      className={`block scroll-py-20 capitalize font-poppins font-regular mb-4 text-[16px] 2xl:text-[16px] 3xl:text-[28px] hover:translate-x-2 duration-300 transition-all duration-[${duration}ms] ${
                        fadeUp
                          ? isMenuOpen || isNavHovered
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-8"
                          : ""
                      }`}
                    >
                      {sub.name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      })}
      {/* Last column: last main service + last three services */}
      <div
        className={`flex flex-col transition-all duration-[${duration}ms] ${
          fadeUp
            ? isMenuOpen || isNavHovered
              ? "opacity-100 translate-y-0"
              : "opacity-0 translate-y-8"
            : ""
        }`}
        key="last-main-with-last-three"
      >
        {/* Render the last main service (e.g. cybersecurity awareness) */}
        {lastMainWithLastThree.length > 0 && (
          <>
            <a
              href={lastMainWithLastThree[0].path}
              className={`font-medium font-oswald mb-6 text-[var(--color-dark)] transition-all duration-[${duration}ms] ${
                fadeUp
                  ? isMenuOpen || isNavHovered
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                  : ""
              }`}
            >
              {lastMainWithLastThree[0].name}
            </a>
            {lastMainWithLastThree[0].children && (
              <ul>
                {lastMainWithLastThree[0].children.map((sub) => (
                  <li key={sub.path}>
                    <a
                      href={sub.path}
                      className={`block scroll-py-20 capitalize font-poppins font-regular mb-4 text-[16px] 2xl:text-[16px] 3xl:text-[28px] hover:translate-x-2 duration-300 transition-all duration-[${duration}ms] ${
                        fadeUp
                          ? isMenuOpen || isNavHovered
                            ? "opacity-100 translate-y-0"
                            : "opacity-0 translate-y-8"
                          : ""
                      }`}
                    >
                      {sub.name}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
        {/* Render the last three services in the same column */}
        {lastMainWithLastThree.slice(1).map((service) => (
          <a
            key={service.path}
            href={service.path}
            className={`font-medium font-oswald mb-6 text-[var(--color-dark)] transition-all duration-[${duration}ms] ${
              fadeUp
                ? isMenuOpen || isNavHovered
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
                : ""
            }`}
          >
            {service.name}
          </a>
        ))}
      </div>
    </div>
  )
}

export default function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isNavHovered, setIsNavHovered] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [isHeaderHovered, setIsHeaderHovered] = useState(false)

  // Animation state for header entrance
  const [headerLoaded, setHeaderLoaded] = useState(false)
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setTimeout(() => setHeaderLoaded(true), 100)
  }, [])

  useEffect(() => {
    setHasMounted(true)
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Get first phone number from first service with a phone (fallback)
  const firstPhone = "https://calendly.com/pietro-domma" // fallback

  // Helper: Split LINKS into main and others for menu dropdown
  const MAIN_LINK_KEYS = ["home", "about", "career", "contact"]
  const mainLinks = Object.entries(LINKS).filter(([key]) =>
    MAIN_LINK_KEYS.includes(key)
  )
  // const otherLinks = Object.entries(LINKS).filter(
  //   ([key]) => !MAIN_LINK_KEYS.includes(key)
  // )

  // Helper to handle nav hover for both nav items and dropdowns
  const navHoverHandlers = {
    onMouseEnter: () => setIsNavHovered(true),
    onMouseLeave: () => setIsNavHovered(false),
  }

  // Prevent hydration mismatch by not rendering until mounted
  if (!hasMounted) return null

  return (
    <>
      <header
        className={`fixed top-0 left-0 w-full z-50 xl:py-8 transition-all duration-300 ${
          isScrolled
            ? `${styles["header-blur"]} border-b border-b-slate-200 `
            : ""
        }
        duration-700 ease-out
        ${
          headerLoaded
            ? "translate-y-0 opacity-100"
            : "-translate-y-8 opacity-0"
        }
        `}
        style={{
          willChange: "transform, opacity",
        }}
        onMouseEnter={() => setIsHeaderHovered(true)} // NEW
        onMouseLeave={() => setIsHeaderHovered(false)} // NEW
      >
        {/* Animated background reveal */}
        <div
          className={`absolute left-0 top-0 w-full h-full z-0 transition-all duration-500 ease-in-out pointer-events-none
            ${
              isHeaderHovered
                ? "bg-white max-h-[500px]"
                : "bg-transparent max-h-0"
            }
          `}
          style={{
            transitionProperty: "background,max-height",
            clipPath: isHeaderHovered
              ? "inset(0% 0% 0% 0%)"
              : "inset(0% 0% 100% 0%)",
          }}
        />
        <div
          className={`relative flex items-center  justify-between px-5 xl:px-16 3xl:px-24 py-2 z-10 transition-colors duration-500 ${
            isHeaderHovered ? "bg-transparent" : "bg-transparent"
          }`}
          {...navHoverHandlers}
        >
          <Link href="/" className="flex items-center" aria-label="Cyberstone home">
            <div
              className={` font-bold flex items-center gap-3 uppercase xl:w-[325px]  3xl:w-[435px] ${
                isScrolled || isNavHovered
                  ? "text-[var(--color-dark)]"
                  : "text-white"
              }`}
            >
              <svg
                viewBox="0 0 687 153"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="w-full h-full max-w-48 xl:max-w-full"
              >
                <path
                  d="M76.3195 24.6201L76.2695 24.6501C76.2695 24.6501 76.2995 24.6501 76.3095 24.6401V24.6201H76.3195Z"
                  fill="#E42986"
                />
                <path
                  d="M133.41 15.6001L119.43 27.5901C120.51 27.7801 121.59 27.9501 122.68 28.1001C125.72 28.5201 128.77 28.7701 131.72 28.7701C132.01 28.7701 132.3 28.7701 132.58 28.7701C133.12 28.7701 133.65 28.7501 134.18 28.7201C134.97 28.6801 135.75 28.6201 136.53 28.5401C136.92 28.5001 137.31 28.4601 137.69 28.4101C138.84 28.2701 139.97 28.0801 141.09 27.8601C145.43 27.0201 149.54 25.6501 153.3 23.9901L133.51 15.6101C133.51 15.6101 133.45 15.6101 133.42 15.6101L133.41 15.6001Z"
                  fill="#F69321"
                />
                <path
                  d="M159.5 20.85C159.74 20.71 159.97 20.58 160.21 20.44C167.68 16.02 172.9 10.97 174.43 8.27998C174.73 7.75998 175.09 6.98998 174.77 6.35998C174.62 6.08998 174.43 5.90998 174.19 5.82998C173.37 5.53998 171.93 6.26998 169.3 7.58998C166.88 8.79998 163.67 10.4 159.77 11.87C155.91 13.32 151.37 14.65 146.26 15.34C144.76 15.55 143.22 15.7 141.63 15.78C141.54 15.78 141.44 15.78 141.35 15.79C139.55 15.88 137.69 15.88 135.77 15.77C135.01 15.73 134.26 15.67 133.5 15.6L153.29 23.98C154.99 23.23 156.61 22.43 158.15 21.59C158.61 21.34 159.05 21.09 159.49 20.84L159.5 20.85Z"
                  fill="#FCB217"
                />
                <path
                  d="M37.7003 13.43C36.1703 13.23 34.6603 12.99 33.2003 12.71C32.4203 12.56 31.6403 12.4 30.8803 12.23C28.6503 11.74 26.5103 11.17 24.4803 10.55C23.8903 10.37 23.3003 10.19 22.7303 9.99996C19.3003 8.88996 16.2003 7.65996 13.5303 6.45996L37.8103 27.23C37.8103 27.23 37.8603 27.23 37.8903 27.24L45.1103 14.01C42.5903 13.94 40.1203 13.74 37.7203 13.42L37.7003 13.43Z"
                  fill="#E02B96"
                />
                <path
                  d="M76.3198 24.6201L79.5798 6.87012C76.4698 8.10012 72.8998 9.37012 69.0898 10.5101C68.6298 10.6501 68.1598 10.7801 67.6898 10.9201C65.3198 11.5901 62.8598 12.2001 60.3698 12.6901C58.9598 12.9701 57.5498 13.2101 56.1298 13.4101C54.6398 13.6201 53.1398 13.7801 51.6498 13.8901C50.8498 13.9401 50.0598 13.9801 49.2798 14.0101C48.5198 14.0401 47.7598 14.0501 47.0098 14.0501C46.3698 14.0501 45.7298 14.0401 45.0898 14.0201L75.5698 24.8501C75.8098 24.7801 76.0398 24.7201 76.2698 24.6501L76.3198 24.6201Z"
                  fill="#CF2226"
                />
                <path
                  d="M102.58 6.78003L76.3301 24.62V24.64C79.1001 23.86 81.6401 23.04 83.8301 22.28C84.5401 22.03 85.2201 21.79 85.8501 21.56C86.9901 21.15 87.9901 20.77 88.8301 20.43C90.2201 19.89 91.1901 19.47 91.6601 19.27C92.1301 19.47 93.1301 19.88 94.5301 20.43C95.4401 20.78 96.5201 21.19 97.7501 21.64C98.9001 22.06 100.18 22.5 101.56 22.96C102.18 23.17 102.83 23.38 103.49 23.59C103.75 23.67 104 23.75 104.26 23.83C107.79 24.93 111.8 26.04 116 26.93C116.99 27.14 117.99 27.34 118.99 27.52L102.72 6.85003C102.72 6.85003 102.62 6.81003 102.57 6.79003L102.58 6.78003Z"
                  fill="#EF5527"
                />
                <path
                  d="M45.0901 14.02L37.8701 27.25C39.0601 27.43 40.2801 27.6 41.5401 27.75C43.9201 28.03 46.4201 28.24 49.0301 28.34C50.1201 28.38 51.2301 28.41 52.3501 28.42H52.5701C54.9301 28.42 57.3201 28.27 59.7001 28.01C61.3301 27.83 62.9601 27.6 64.5701 27.33C64.8001 27.29 65.0301 27.25 65.2601 27.21C65.9901 27.08 66.7201 26.95 67.4401 26.8C70.2801 26.24 73.0201 25.56 75.5801 24.86L45.1001 14.03L45.0901 14.02Z"
                  fill="#A9213F"
                />
                <path
                  d="M102.58 6.7799C101.86 6.4699 101.17 6.1599 100.52 5.8599C99.8003 5.5299 99.1103 5.2099 98.4603 4.8999C95.7503 3.6099 93.7703 2.5699 92.7703 2.0199C92.3203 1.7699 92.0703 1.6299 92.0403 1.6199L91.6703 1.3999L91.3003 1.6099C91.3003 1.6099 91.0003 1.7699 90.4903 2.0199C89.6303 2.4499 88.1203 3.1999 86.1103 4.1099C84.9303 4.6399 83.5903 5.2299 82.1003 5.8499C81.3003 6.1799 80.4603 6.5199 79.5803 6.8699L76.3203 24.6199L102.57 6.7799H102.58Z"
                  fill="#EA3228"
                />
                <path
                  d="M132.67 15.5301C131.21 15.3701 129.75 15.1601 128.29 14.9101C126.49 14.6001 124.71 14.2201 122.94 13.8001C119.75 13.0301 116.63 12.1101 113.68 11.1101C110.4 10.0001 107.33 8.8101 104.59 7.6501C104.57 7.6501 104.55 7.6301 104.53 7.6201C103.91 7.3601 103.31 7.1001 102.73 6.8501L119 27.5201C119.14 27.5501 119.29 27.5701 119.43 27.6001L133.41 15.6101C133.16 15.5901 132.92 15.5601 132.67 15.5301Z"
                  fill="#F56F1E"
                />
                <path
                  d="M40.1604 117.82L26.6904 112.84C27.0604 113.34 27.4304 113.83 27.8104 114.32C28.3804 115.08 28.9604 115.82 29.5404 116.56C31.7304 119.34 33.9404 121.96 36.1304 124.42C38.2704 126.83 40.3904 129.09 42.4704 131.19C43.3204 132.05 44.1704 132.89 45.0004 133.7C45.0604 133.75 45.1104 133.81 45.1704 133.86C46.0304 134.69 46.8704 135.49 47.7004 136.27C47.8304 136.39 47.9704 136.52 48.1004 136.64C48.3104 136.83 48.5204 137.03 48.7304 137.22L40.3204 118.04C40.2704 117.97 40.2304 117.9 40.1804 117.83L40.1604 117.82Z"
                  fill="#2D58A7"
                />
                <path
                  d="M13.5103 6.46C13.1503 6.3 12.8003 6.14 12.4603 5.98C11.4603 5.52 10.5303 5.07 9.66027 4.64C7.63027 3.63 5.99027 2.71 4.81027 2.02C3.46027 1.22 2.71027 0.73 2.67027 0.7L1.66027 0L1.49027 1.22C1.49027 1.22 1.45027 1.53 1.39027 2.02C1.18027 3.71 0.710273 7.97 0.380273 13.92C0.270273 15.91 0.170273 18.1 0.110273 20.43C0.0702734 21.69 0.0502734 23 0.0302734 24.35L13.5203 6.47H13.5103V6.46Z"
                  fill="#E42986"
                />
                <path
                  d="M15.6003 25.6C15.7103 23.62 15.8303 22.1 15.9103 21.17C17.5403 21.88 21.1203 23.31 26.1503 24.72C26.4003 24.79 26.6503 24.86 26.9103 24.93C27.9103 25.2 28.9603 25.47 30.0603 25.73C30.0803 25.73 30.0903 25.73 30.1103 25.74C30.2703 25.78 30.4403 25.82 30.6103 25.86C32.8203 26.37 35.2203 26.84 37.7903 27.24L13.5203 6.46997L0.0302734 24.35C0.0302734 24.35 0.0302734 24.38 0.0302734 24.4L15.6003 25.6Z"
                  fill="#B91D7F"
                />
                <path
                  d="M50.8 132.2C50.72 132.1 50.64 132.01 50.56 131.91C50.37 131.67 50.17 131.43 49.97 131.18C49.65 130.78 49.32 130.37 48.99 129.95C48.64 129.51 48.29 129.06 47.94 128.61C45.74 125.78 43.47 122.69 41.18 119.34C40.88 118.91 40.58 118.47 40.29 118.03L48.7 137.21C48.7 137.21 48.75 137.26 48.78 137.29L55.74 138.04C54.17 136.27 52.51 134.32 50.78 132.2H50.8Z"
                  fill="#1C73B1"
                />
                <path
                  d="M67.36 149.86C67.27 149.78 67.17 149.69 67.06 149.59C66.88 149.42 66.68 149.24 66.46 149.05C66.34 148.94 66.22 148.83 66.09 148.71C64.08 146.84 61.01 143.85 57.31 139.78C56.8 139.22 56.28 138.64 55.75 138.04L48.79 137.29C50.86 139.19 52.82 140.9 54.66 142.44C58.39 145.57 61.57 147.96 63.87 149.59C64 149.68 64.12 149.77 64.25 149.86C64.54 150.07 64.82 150.26 65.08 150.44C65.35 150.63 65.61 150.8 65.84 150.96C67.07 151.8 67.76 152.23 67.81 152.26L68.69 151.01C68.69 151.01 68.19 150.59 67.37 149.86H67.36Z"
                  fill="#1D9BCF"
                />
                <path
                  d="M2.66992 59.55C2.98992 61.37 3.34992 63.1901 3.73992 65.0101C3.74992 65.0801 3.76992 65.1501 3.77992 65.2201C4.30992 67.6401 4.90992 70.05 5.58992 72.43C5.91992 73.6101 6.27992 74.7701 6.64992 75.93C7.01992 77.07 7.39992 78.2101 7.80992 79.3301C8.85992 82.2601 10.0499 85.12 11.3899 87.88C11.4899 88.1 11.5999 88.31 11.6999 88.53C12.2899 89.72 12.8899 90.8901 13.5099 92.0501C13.7999 92.5901 14.0899 93.13 14.3799 93.67L25.3799 91.6501L2.66992 59.55Z"
                  fill="#392C72"
                />
                <path
                  d="M25.4004 91.6501C24.2204 89.1101 23.0804 86.4901 21.9804 83.7901C21.9604 83.7401 21.9404 83.6901 21.9204 83.6501C20.9304 81.2101 20.0804 78.6301 19.3604 75.9401C18.9804 74.5401 18.6304 73.1101 18.3204 71.6601C18.1704 70.9601 18.0204 70.25 17.8804 69.5401C17.5704 67.9901 17.3004 66.43 17.0604 64.8601C16.6804 62.42 16.3804 59.9601 16.1304 57.5201C16.0004 56.1701 15.8804 54.8201 15.7804 53.4901C15.7104 52.5901 15.6504 51.69 15.6004 50.8L2.65039 59.43C2.65039 59.43 2.66039 59.5001 2.67039 59.5401L25.3904 91.64H25.4004V91.6501Z"
                  fill="#672A78"
                />
                <path
                  d="M25.3896 91.6499L14.3896 93.6699C14.5096 93.8999 14.6396 94.1299 14.7596 94.3499C16.3596 97.2399 18.0096 100.03 19.7196 102.71C19.9196 103.03 20.1296 103.35 20.3296 103.66C20.8696 104.49 21.4096 105.31 21.9496 106.13C23.4896 108.43 25.0596 110.64 26.6496 112.77C26.6496 112.77 26.6596 112.79 26.6696 112.8L25.4396 91.7099L25.3996 91.6599L25.3896 91.6499Z"
                  fill="#282667"
                />
                <path
                  d="M15.2303 39.1099V38.8399C15.2303 37.9999 15.2303 37.1799 15.2403 36.3799C15.2403 36.3499 15.2403 36.3199 15.2403 36.2799C15.2403 36.0499 15.2403 35.8299 15.2403 35.5999C15.2703 32.8899 15.3503 30.4199 15.4503 28.2999C15.4903 27.3199 15.5403 26.4199 15.5903 25.5899L0.0302734 24.3999C0.0302734 24.3999 0.0302734 24.4199 0.0302734 24.4299L15.5903 50.7999C15.3403 46.6999 15.2403 42.7399 15.2303 39.1099Z"
                  fill="#A03794"
                />
                <path
                  d="M36.8797 112.77C35.9597 111.31 35.0397 109.81 34.1397 108.26C32.6697 105.78 31.2297 103.21 29.8097 100.53C29.7297 100.39 29.6597 100.25 29.5797 100.1C29.4597 99.87 29.3397 99.65 29.2197 99.42C28.6897 98.41 28.1697 97.38 27.6497 96.33C27.3197 95.68 26.9997 95.02 26.6797 94.35C26.2597 93.48 25.8397 92.6 25.4297 91.71L26.6597 112.79C26.6697 112.81 26.6797 112.82 26.6997 112.84L40.1697 117.82C39.0797 116.19 37.9797 114.51 36.8897 112.77H36.8797Z"
                  fill="#2D3D98"
                />
                <path
                  d="M15.5896 50.7999L0.0295638 24.4299C0.00956377 26.3499 0.00956377 28.3599 0.0395638 30.4299C0.0495638 31.0699 0.0595638 31.7199 0.0795638 32.3699C0.0895638 32.9699 0.109564 33.5699 0.129564 34.1799C0.129564 34.2099 0.129564 34.2399 0.129564 34.2799C0.179564 35.7799 0.249564 37.3099 0.329564 38.8599C0.329564 38.9499 0.329564 39.0399 0.339564 39.1299C0.529564 42.5399 0.819564 46.0599 1.21956 49.6399C1.50956 52.2499 1.86956 54.8899 2.29956 57.5499C2.39956 58.1899 2.50956 58.8299 2.61956 59.4699L15.5696 50.8399C15.5696 50.8399 15.5696 50.8299 15.5696 50.8199L15.5896 50.7999Z"
                  fill="#7E2A90"
                />
                <path
                  d="M158.71 34.5601V46.6901H146.57V46.5801L158.48 34.6701L158.59 34.5601H158.71Z"
                  fill="#B91D7F"
                />
                <path
                  d="M146.57 34.5601V46.5801L158.59 34.5601H146.57Z"
                  fill="#E02B96"
                />
                <path
                  d="M170.94 27.22V36.37H161.79V36.28L170.77 27.3L170.85 27.22H170.94Z"
                  fill="#FCB217"
                />
                <path
                  d="M161.79 27.22V36.28L170.85 27.22H161.79Z"
                  fill="#F69321"
                />
                <path
                  d="M173.11 48.1499V59.4699H161.79V59.3599L172.9 48.2499L173 48.1499H173.11Z"
                  fill="#A9213F"
                />
                <path
                  d="M161.79 48.1499V59.3599L173 48.1499H161.79Z"
                  fill="#CF2226"
                />
                <path
                  d="M149.74 49.5V55.93H143.31V55.87L149.62 49.56L149.68 49.5H149.74Z"
                  fill="#1D9BCF"
                />
                <path
                  d="M143.31 49.5V55.87L149.68 49.5H143.31Z"
                  fill="#1C73B1"
                />
                <path
                  d="M140.7 39.21H103.93L140.7 57.42V39.21Z"
                  fill="#FCB217"
                />
                <path
                  d="M143.66 118.96C143.66 117.9 143.63 116.86 143.57 115.84C143.57 115.82 143.57 115.79 143.57 115.77C143.54 115.36 143.52 114.96 143.48 114.56C143.43 113.93 143.36 113.31 143.29 112.7C143.25 112.4 143.21 112.11 143.17 111.82C143.08 111.19 142.97 110.57 142.86 109.96C142.37 107.42 141.67 105.04 140.75 102.83C140.63 102.53 140.5 102.24 140.37 101.95C140.35 101.9 140.33 101.85 140.3 101.81C140.22 101.63 140.14 101.46 140.05 101.29C140.01 101.21 139.98 101.14 139.94 101.06C139.23 99.62 138.38 98.27 137.42 97C136.7 96.04 135.91 95.14 135.06 94.3C134.86 94.1 134.66 93.91 134.45 93.71C133.72 93.02 132.94 92.38 132.13 91.77L124.51 111.68C124.51 111.68 124.53 111.75 124.54 111.78L143.66 119.02C143.66 119.02 143.66 118.99 143.66 118.97V118.96Z"
                  fill="#282667"
                />
                <path
                  d="M131.64 91.3999L105.56 104.31H114.71C114.85 104.31 114.99 104.31 115.12 104.31C116.69 104.37 118.19 104.74 119.51 105.36C120 105.59 120.47 105.86 120.9 106.16L120.93 106.24L121.12 106.32C121.38 106.51 121.64 106.72 121.87 106.93C122.14 107.17 122.39 107.43 122.61 107.7C122.67 107.77 122.73 107.85 122.79 107.93C123.16 108.39 123.46 108.89 123.71 109.42C123.84 109.71 123.96 110 124.07 110.31C124.23 110.74 124.37 111.19 124.5 111.66L132.12 91.7499C131.96 91.6299 131.79 91.5099 131.63 91.3899L131.64 91.3999Z"
                  fill="#392C72"
                />
                <path
                  d="M143.66 119.01L124.54 111.77C124.54 111.77 124.54 111.8 124.55 111.82C124.63 112.1 124.7 112.4 124.76 112.7C124.87 113.2 124.97 113.72 125.05 114.27C125.14 114.83 125.2 115.41 125.25 116C125.33 116.94 125.37 117.93 125.37 118.96C125.37 121.92 125.08 124.55 124.52 126.78C124.37 127.39 124.14 127.97 123.86 128.53L123.78 128.58L123.81 128.63C123.81 128.63 123.8 128.65 123.79 128.66L143.65 119.01H143.66Z"
                  fill="#2D3D98"
                />
                <path
                  d="M33.7195 54.26C33.1795 55.29 32.7095 56.36 32.3195 57.46C32.2895 57.54 32.2695 57.61 32.2395 57.69C32.1595 57.92 32.0795 58.16 31.9995 58.4C31.7795 59.09 31.5895 59.8 31.4295 60.53C31.0595 62.15 30.7895 63.86 30.5895 65.66C30.5895 65.69 30.5895 65.71 30.5895 65.74C30.4695 66.92 30.3795 68.13 30.3295 69.37C30.2995 70.2 30.2695 71.03 30.2695 71.88C30.2695 72.56 30.2795 73.22 30.2995 73.87C30.3195 74.39 30.3395 74.9 30.3595 75.4C30.3595 75.46 30.3595 75.52 30.3695 75.57C30.3695 75.67 30.3795 75.77 30.3895 75.87C30.4795 77.28 30.6095 78.61 30.7895 79.87C31.0295 81.64 31.4395 83.35 31.9995 85C32.5395 86.58 33.2095 88.1 34.0095 89.53L48.5095 72L33.7195 54.25V54.26Z"
                  fill="#A9213F"
                />
                <path
                  d="M48.5297 71.98C48.5297 71.98 48.5297 71.91 48.5297 71.88C48.5297 69.1 48.7697 66.63 49.2497 64.52C49.2897 64.35 49.3297 64.18 49.3797 64.01C49.3997 63.94 49.4197 63.87 49.4497 63.8C49.4497 63.77 49.4697 63.75 49.4797 63.72C49.4997 63.66 49.5197 63.59 49.5497 63.53C49.5497 63.51 49.5597 63.49 49.5697 63.48C49.5897 63.41 49.6197 63.35 49.6397 63.29C49.6397 63.27 49.6497 63.25 49.6597 63.24C49.7897 62.91 49.9497 62.6 50.1297 62.29C50.3597 61.9 50.6197 61.52 50.9097 61.17H50.9397C51.0997 60.97 51.2597 60.79 51.4097 60.63V60.6C51.6697 60.34 51.9297 60.1 52.2097 59.88C52.2497 59.85 52.2797 59.81 52.3197 59.79C52.3497 59.77 52.3697 59.75 52.3897 59.73C52.4097 59.71 52.4297 59.69 52.4497 59.68C52.4597 59.67 52.4797 59.66 52.4997 59.64C52.4997 59.64 52.5197 59.63 52.5297 59.62C52.8897 59.35 53.2697 59.1 53.6697 58.87C53.7197 58.84 53.7797 58.81 53.8397 58.78C54.8597 58.23 55.9997 57.84 57.1997 57.64L33.7197 54.26C33.7197 54.26 33.7197 54.26 33.7197 54.27L48.5097 72.02L48.5297 71.99V71.98Z"
                  fill="#CF2226"
                />
                <path
                  d="M58.6298 86.05L58.6698 86.11L59.0398 86.06C58.1998 86.05 57.3798 85.94 56.5898 85.75C53.8498 85.07 51.5198 83.32 50.1298 80.98C50.0498 80.85 49.9798 80.71 49.8998 80.57V80.55L49.8698 80.52C49.5598 79.92 49.3098 79.28 49.1198 78.61C49.0098 78.22 48.9298 77.81 48.8698 77.4C48.7998 76.91 48.7498 76.4 48.6998 75.88C48.6998 75.78 48.6798 75.68 48.6698 75.58C48.6698 75.56 48.6698 75.53 48.6698 75.51C48.6298 75.03 48.5998 74.54 48.5798 74.04C48.5498 73.4 48.5298 72.73 48.5298 72.04L48.5098 72.01L34.0098 89.54C34.0398 89.59 34.0698 89.64 34.0898 89.69L58.6598 86.11L58.6198 86.05H58.6298Z"
                  fill="#E02B96"
                />
                <path
                  d="M34.0996 89.6901C34.0996 89.6901 34.1296 89.7401 34.1396 89.7701C34.5896 90.5601 35.0696 91.3201 35.5896 92.0601C35.9796 92.6201 36.3996 93.1701 36.8296 93.7001C36.9896 93.9001 37.1596 94.0901 37.3196 94.2901C37.5796 94.5901 37.8496 94.8901 38.1196 95.1801C38.2196 95.2801 38.3096 95.3901 38.4096 95.4901L38.3896 95.5201H38.4296C39.7396 96.8701 41.1796 98.0901 42.7296 99.1701C42.8096 99.2301 42.8996 99.2901 42.9796 99.3401C44.6196 100.45 46.3696 101.39 48.2096 102.15C48.8296 102.41 49.4696 102.65 50.1196 102.86C52.1496 103.53 54.2696 103.98 56.4496 104.19C56.4796 104.19 56.5096 104.19 56.5396 104.19C57.3996 104.27 58.2796 104.31 59.1596 104.31H69.1496L58.6696 86.1101L34.0996 89.6901Z"
                  fill="#B91D7F"
                />
                <path
                  d="M95.5896 57.4599H95.9196H96.0596H140.7V57.4199L103.93 39.2099H103.92L103.89 39.2V39.2099L95.5596 57.4599H95.5896Z"
                  fill="#F69321"
                />
                <path
                  d="M113.48 86.0601H114.7C114.79 86.0601 114.89 86.0601 114.98 86.0601C117.34 86.0801 119.66 86.3801 121.88 86.9301C122.13 86.9901 122.38 87.0601 122.62 87.1201C124.09 87.5201 125.51 88.0401 126.87 88.6601C126.97 88.7101 127.08 88.7501 127.18 88.8001C128.75 89.5301 130.24 90.4101 131.63 91.4001L105.55 104.31H105.12L96.79 86.0601H113.47H113.48Z"
                  fill="#672A78"
                />
                <path
                  d="M48.5395 72.03C48.5395 72.03 48.5395 72 48.5395 71.98L48.5195 72.01L48.5395 72.04V72.03Z"
                  fill="#EA3228"
                />
                <path
                  d="M57.3502 57.6L67.9302 39.22H59.8902H59.1102C59.0302 39.22 58.9602 39.22 58.8802 39.22C58.7402 39.22 58.5902 39.22 58.4502 39.22C58.3302 39.22 58.2202 39.22 58.1002 39.22L58.0802 39.27L57.9902 39.22C57.4702 39.24 56.9602 39.27 56.4502 39.32C54.6202 39.49 52.8302 39.82 51.1002 40.31C50.7702 40.4 50.4402 40.5 50.1102 40.61C49.6502 40.76 49.1902 40.92 48.7402 41.09C47.2602 41.65 45.8402 42.32 44.4902 43.1C42.6702 44.15 40.9802 45.39 39.4502 46.79C38.3202 47.83 37.2802 48.96 36.3302 50.16C36.3002 50.2 36.2602 50.25 36.2302 50.29C35.4502 51.3 34.7402 52.35 34.1202 53.46C33.9802 53.71 33.8402 53.97 33.7002 54.23L57.1802 57.61C57.1802 57.61 57.2802 57.59 57.3302 57.59L57.3502 57.6Z"
                  fill="#EA3228"
                />
                <path
                  d="M67.9704 39.21H67.9404L57.3604 57.59C57.9304 57.5 58.5203 57.46 59.1203 57.46H91.0804H91.1003H95.5704L95.5903 57.41L68.0704 39.21H67.9704Z"
                  fill="#EF5527"
                />
                <path
                  d="M68.0703 39.21L95.5903 57.41L103.89 39.21H68.0703Z"
                  fill="#F56F1E"
                />
                <path
                  d="M69.4799 86.0601H96.7899L96.8299 86.1401L69.8299 104.31H69.1499L58.6699 86.1101L59.0399 86.0601C59.0399 86.0601 59.1199 86.0601 59.1699 86.0601H69.4899H69.4799Z"
                  fill="#A03794"
                />
                <path
                  d="M96.8303 86.1399L69.8203 104.31H105.12L96.8303 86.1399Z"
                  fill="#7E2A90"
                />
                <path
                  d="M123.71 128.84C123.71 128.84 123.69 128.87 123.69 128.88C123.48 129.25 123.24 129.61 122.97 129.95C122.92 130.02 122.87 130.08 122.81 130.14C122.75 130.22 122.68 130.29 122.62 130.37C122.39 130.63 122.14 130.88 121.88 131.12C121.7 131.28 121.52 131.43 121.34 131.58C120.8 132.01 120.21 132.38 119.58 132.7C118.64 133.17 117.61 133.52 116.53 133.7C115.92 133.81 115.28 133.86 114.64 133.86H107.31L132.69 145.46L123.7 128.84H123.71Z"
                  fill="#1C73B1"
                />
                <path
                  d="M107.32 133.86H107.17V152.11H114.65C116.09 152.11 117.5 152.01 118.88 151.81C119.62 151.71 120.35 151.57 121.07 151.41C121.59 151.3 122.1 151.17 122.61 151.03C123.37 150.82 124.11 150.58 124.84 150.31C125.27 150.15 125.7 149.98 126.12 149.81C126.33 149.72 126.53 149.63 126.73 149.54C127.7 149.1 128.64 148.62 129.54 148.08C130.69 147.4 131.78 146.65 132.81 145.82C132.81 145.82 132.82 145.82 132.83 145.81C132.84 145.81 132.85 145.79 132.86 145.78L132.69 145.47L107.31 133.87L107.32 133.86Z"
                  fill="#1D9BCF"
                />
                <path
                  d="M132.87 145.77C132.93 145.72 132.99 145.67 133.05 145.62L132.7 145.46L132.87 145.77Z"
                  fill="#EA3228"
                />
                <path
                  d="M123.8 128.67C123.77 128.73 123.74 128.78 123.71 128.84L132.7 145.46L133.05 145.62C134 144.85 134.89 144.01 135.73 143.12C135.73 143.11 135.75 143.1 135.76 143.09C137.31 141.44 138.65 139.59 139.76 137.58C140.11 136.94 140.44 136.27 140.75 135.59C141.35 134.25 141.84 132.86 142.21 131.41C142.22 131.37 142.23 131.33 142.24 131.29C142.25 131.23 142.27 131.18 142.28 131.12C142.38 130.74 142.47 130.35 142.55 129.95C142.69 129.32 142.81 128.68 142.92 128.04C143.1 127.02 143.24 125.97 143.35 124.9C143.55 123.02 143.65 121.06 143.65 119.03L123.79 128.68L123.8 128.67Z"
                  fill="#2D58A7"
                />
                <path
                  d="M231.94 56.2101C238.12 56.2101 242.5 58.7301 245.08 63.7601H258.96C256.88 58.0101 253.42 53.5501 248.57 50.3801C243.73 47.2101 238.18 45.6201 231.94 45.6201C223.73 45.6201 216.79 48.3801 211.13 53.9101C205.46 59.4401 202.63 66.4501 202.63 74.9301C202.63 83.4101 205.45 90.4201 211.09 95.9501C216.73 101.48 223.68 104.24 231.94 104.24C238.18 104.24 243.72 102.67 248.57 99.5201C253.41 96.3701 256.88 91.9301 258.96 86.1801H245.08C242.45 91.2201 238.07 93.7301 231.94 93.7301C226.74 93.7301 222.51 92.0201 219.25 88.6001C215.99 85.1801 214.37 80.6401 214.37 74.9701C214.37 69.3001 216 64.7601 219.25 61.3401C222.51 57.9201 226.73 56.2101 231.94 56.2101Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M287.94 90.61L276.36 58.26H263.39L281.2 102.93L271.76 125.26H283.83L312.24 58.26H300.17L287.94 90.61Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M344.75 57.61C338.51 57.61 333.77 60.18 330.55 65.33V43H318.97V103.76H330.55V96.12C333.94 101.65 338.72 104.41 344.88 104.41C351.04 104.41 356.14 102.22 360.19 97.84C364.24 93.46 366.27 87.84 366.27 80.97C366.27 74.1 364.24 68.49 360.19 64.14C356.14 59.79 350.99 57.61 344.75 57.61ZM350.95 90.78C348.57 93.19 345.76 94.39 342.53 94.39C339.3 94.39 336.5 93.2 334.11 90.82C331.73 88.44 330.54 85.17 330.54 81.01C330.54 76.85 331.73 73.58 334.11 71.2C336.49 68.82 339.3 67.63 342.53 67.63C345.76 67.63 348.56 68.81 350.95 71.16C353.33 73.51 354.52 76.77 354.52 80.93C354.52 85.09 353.33 88.38 350.95 90.78Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M394.71 57.6101C388.06 57.6101 382.64 59.7301 378.45 63.9701C374.26 68.2101 372.17 73.8601 372.17 80.9201C372.17 87.9801 374.25 93.6601 378.41 97.9601C382.57 102.26 387.96 104.41 394.58 104.41C399.94 104.41 404.51 103.04 408.29 100.3C412.07 97.5601 414.61 94.0301 415.93 89.7101H403.45C401.53 92.9901 398.58 94.6401 394.58 94.6401C391.62 94.6401 389.19 93.7401 387.27 91.9301C385.35 90.1201 384.26 87.7401 383.99 84.7901H417.16C417.38 83.4201 417.49 81.9401 417.49 80.3601C417.49 73.4601 415.36 67.9501 411.09 63.8201C406.82 59.6901 401.36 57.6201 394.71 57.6201V57.6101ZM384 77.3101C384.38 74.3001 385.55 71.8901 387.49 70.0801C389.43 68.2701 391.85 67.3701 394.76 67.3701C397.67 67.3701 400.14 68.2701 402.19 70.0801C404.24 71.8901 405.35 74.3001 405.51 77.3101H384Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M438.189 66.3101V58.2601H426.609V103.75H438.189V80.9201C438.189 76.7101 439.059 73.7401 440.819 72.0101C442.569 70.2901 445.169 69.4201 448.619 69.4201H451.489V57.6001C445.689 57.6001 441.249 60.5001 438.189 66.3001V66.3101Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M485.27 79.0801C482.72 78.3401 480.17 77.7301 477.59 77.2301C475.02 76.7401 472.83 76.0501 471.02 75.1801C469.21 74.3001 468.31 73.1001 468.31 71.5701C468.31 70.0401 468.91 68.8301 470.12 67.9601C471.32 67.0901 473.02 66.6501 475.21 66.6501C479.59 66.6501 481.94 68.5701 482.27 72.4001H494.09C493.87 67.8601 492.09 64.2601 488.75 61.6001C485.41 58.9501 480.98 57.6201 475.45 57.6201C469.92 57.6201 465.49 58.9601 462.15 61.6401C458.81 64.3201 457.14 67.6301 457.14 71.5701C457.14 74.5301 458.04 76.9201 459.85 78.7501C461.66 80.5801 463.83 81.8601 466.38 82.5701C468.93 83.2801 471.48 83.8801 474.06 84.3801C476.63 84.8701 478.82 85.5701 480.63 86.4701C482.44 87.3701 483.34 88.6001 483.34 90.1601C483.34 91.7201 482.68 92.9801 481.37 93.9401C480.06 94.9001 478.33 95.3801 476.2 95.3801C471.77 95.3801 469.33 93.4601 468.89 89.6301H457.07C457.4 94.3901 459.29 98.0501 462.74 100.59C466.19 103.14 470.61 104.41 476 104.41C481.39 104.41 485.82 103.12 489.3 100.55C492.78 97.9801 494.51 94.6401 494.51 90.5301C494.51 87.4101 493.61 84.9101 491.8 83.0201C489.99 81.1301 487.82 79.8201 485.27 79.0801Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M518.32 93.12C517.61 92.49 517.25 91.44 517.25 89.96V67.71H527.43V58.27H517.25V47.02H505.67V58.27H500.83V67.71H505.67V89.96C505.67 94.89 506.93 98.42 509.45 100.55C511.97 102.68 515.55 103.75 520.21 103.75H527.52V94.06H522.02C520.27 94.06 519.04 93.75 518.33 93.12H518.32Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M556.87 57.6101C550.22 57.6101 544.65 59.7401 540.16 64.0101C535.67 68.2801 533.43 73.9201 533.43 80.9201C533.43 87.9201 535.65 93.5901 540.08 97.9201C544.51 102.24 550.07 104.41 556.75 104.41C563.43 104.41 569.02 102.25 573.54 97.9201C578.06 93.6001 580.31 87.9301 580.31 80.9201C580.31 73.9101 578.06 68.2801 573.58 64.0101C569.09 59.7401 563.52 57.6101 556.87 57.6101ZM565.08 91.0601C562.75 93.4401 559.98 94.6301 556.75 94.6301C553.52 94.6301 550.78 93.4501 548.54 91.1001C546.29 88.7501 545.17 85.3801 545.17 81.0001C545.17 76.6201 546.29 73.2601 548.54 70.9001C550.78 68.5501 553.53 67.3701 556.79 67.3701C560.05 67.3701 562.82 68.5501 565.12 70.9001C567.42 73.2501 568.57 76.6101 568.57 80.9601C568.57 85.3101 567.41 88.6801 565.08 91.0601Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M615.2 57.6101C608.69 57.6101 603.98 60.1001 601.08 65.0801V58.2701H589.5V103.76H601.08V78.6401C601.08 75.0801 602 72.3601 603.83 70.4701C605.66 68.5801 608.14 67.6401 611.26 67.6401C614.38 67.6401 616.84 68.5801 618.65 70.4701C620.46 72.3601 621.36 75.0801 621.36 78.6401V103.76H632.85V76.9901C632.85 70.9101 631.21 66.1701 627.92 62.7401C624.64 59.3201 620.39 57.6101 615.19 57.6101H615.2Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
                <path
                  d="M686.88 80.3501C686.88 73.4501 684.75 67.9401 680.48 63.8101C676.21 59.6801 670.75 57.6101 664.1 57.6101C657.45 57.6101 652.03 59.7301 647.84 63.9701C643.65 68.2101 641.56 73.8601 641.56 80.9201C641.56 87.9801 643.64 93.6601 647.8 97.9601C651.96 102.26 657.35 104.41 663.97 104.41C669.33 104.41 673.9 103.04 677.68 100.3C681.46 97.5601 684 94.0301 685.32 89.7101H672.84C670.92 92.9901 667.97 94.6401 663.97 94.6401C661.01 94.6401 658.58 93.7401 656.66 91.9301C654.74 90.1201 653.65 87.7401 653.38 84.7901H686.55C686.77 83.4201 686.88 81.9401 686.88 80.3601V80.3501ZM653.38 77.3101C653.76 74.3001 654.93 71.8901 656.87 70.0801C658.81 68.2701 661.23 67.3701 664.14 67.3701C667.05 67.3701 669.52 68.2701 671.57 70.0801C673.62 71.8901 674.73 74.3001 674.89 77.3101H653.38Z"
                  fill={
                    isHeaderHovered
                      ? "var(--color-navy)"
                      : isScrolled
                      ? "var(--color-navy)"
                      : "white"
                  }
                />
              </svg>
              <span className="sr-only">Home</span>
            </div>
          </Link>

          {/* Navigation */}
          <nav className="hidden xl:flex space-x-10 2xl:space-x-16 3xl:space-x-36 ">
            {Object.entries(LINKS).map(([key, value]) =>
              key !== "services" ? (
                <a
                  key={value.path}
                  href={value.path}
                  className={`hover:text-[var(--color-danger)] transition-colors duration-300 font-oswald  text-[16px] 2xl:text-[28px] font-medium uppercase ${
                    isScrolled || isNavHovered
                      ? "text-[var(--color-dark)]"
                      : "text-white"
                  }`}
                  {...navHoverHandlers}
                >
                  {value.name}
                  {/* Add arrow if dropdown exists */}
                  {hasChildren(value) && (
                    <span
                      className={`ml-2 inline-block align-middle duration-300 ease-in-out ${
                        isNavHovered ? "rotate-180" : ""
                      }`}
                    >
                      <svg
                        className="w-[20px] h-[27px] scale-[.7] arrowdn"
                        viewBox="0 0 10 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.9369 13.2494L5.39367 16.7926C5.17624 17.0101 4.82371 17.0101 4.60628 16.7926L1.06305 13.2494C0.845619 13.032 0.845619 12.6794 1.06305 12.462C1.28048 12.2446 1.633 12.2446 1.85043 12.462L4.44321 15.0548L4.44321 1.50678C4.44321 1.19928 4.69248 0.950012 4.99998 0.950012C5.30747 0.950012 5.55674 1.19928 5.55674 1.50678L5.55674 15.0548L8.14952 12.462C8.36695 12.2446 8.71947 12.2446 8.9369 12.462C9.15433 12.6794 9.15433 13.032 8.9369 13.2494Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span>
                  )}
                </a>
              ) : (
                <div key={value.path} className="group" {...navHoverHandlers}>
                  <button
                    className={`hover:text-[var(--color-danger)] transition-colors duration-300 font-oswald  text-[16px] 2xl:text-[28px] font-medium uppercase ${
                      isScrolled || isNavHovered
                        ? "text-[var(--color-dark)]"
                        : "text-white"
                    } flex items-center`}
                    type="button"
                  >
                    {value.name}
                    {/* Down arrow for dropdown, up when open */}
                    {/* <span
                      className={`block align-middle duration-300 ease-in-out ms-4 ${
                        isNavHovered ? "rotate-180" : ""
                      }`}
                    >
                      <svg
                        className="w-[20px] h-[27px]  scale-[.9] arrowdn"
                        viewBox="0 0 10 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M8.9369 13.2494L5.39367 16.7926C5.17624 17.0101 4.82371 17.0101 4.60628 16.7926L1.06305 13.2494C0.845619 13.032 0.845619 12.6794 1.06305 12.462C1.28048 12.2446 1.633 12.2446 1.85043 12.462L4.44321 15.0548L4.44321 1.50678C4.44321 1.19928 4.69248 0.950012 4.99998 0.950012C5.30747 0.950012 5.55674 1.19928 5.55674 1.50678L5.55674 15.0548L8.14952 12.462C8.36695 12.2446 8.71947 12.2446 8.9369 12.462C9.15433 12.6794 9.15433 13.032 8.9369 13.2494Z"
                          fill="currentColor"
                        />
                      </svg>
                    </span> */}
                  </button>
                  {/* Show grid on hover */}
                  <div
                    className="absolute dropdown left-0 top-full w-screen text-white p-0 z-20 overflow-hidden transition-all duration-500 ease-in-out max-h-0 xl:group-hover:max-h-[80vh]"
                    onMouseEnter={() => setIsNavHovered(true)}
                    onMouseLeave={() => setIsNavHovered(false)}
                  >
                    {/* Use ServicesGrid for dropdown */}
                    <ServicesGrid
                      services={(LINKS.services.children || []) as LinkType[]}
                      fadeUp={true}
                      isMenuOpen={isMenuOpen}
                      isNavHovered={isNavHovered}
                    />
                  </div>
                </div>
              )
            )}
          </nav>

          {/* Buttons */}
          <div className="flex items-center space-x-4">
            <Button
              type="one"
              target="_blank"
              url={`${firstPhone}`}
              title={`Let's talk`}
              icon={false}
            />

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`flex uppercase font-oswald  text-[16px]  2  3xl:text-[38px] items-center justify-center gap-4 py-2  xl:py-5 px-4  xl:px-10 bg-[var(--color-dark)] rounded-full ${
                isNavHovered
                  ? "text-white bg-[var(--color-danger)]"
                  : "text-white"
              } transition-colors duration-300 hover:text-[var(--color-dark)] hover:bg-[--color-danger]`}
            >
              <span>menu</span>
              <div className="flex  flex-col gap-1 xl:gap-2 humberg">
                <span className="h-[2px] xl:h-[4px] w-[30px] xl:w-[40px]">
                  &nbsp;
                </span>
                <span className="h-[2px] xl:h-[4px] w-[30px] xl:w-[40px]">
                  &nbsp;
                </span>
                <span className="h-[2px] xl:h-[4px] w-[30px] xl:w-[40px]">
                  &nbsp;
                </span>
              </div>
            </button>
          </div>

          {/* Grid layout for Menu Button */}
          <div
            className={`menudrop absolute left-0 top-full w-screen text-white p-0 z-20
              overflow-hidden transition-all duration-500 ease-in-out
              ${isMenuOpen ? "xl:max-h-[80vh]" : "max-h-0"}
              xl:overflow-hidden
              ${
                isMenuOpen
                  ? "max-h-[80vh] overflow-y-auto xl:overflow-hidden"
                  : ""
              }
            `}
            onMouseEnter={() => setIsMenuOpen(true)}
            onMouseLeave={() => setIsMenuOpen(false)}
          >
            {/* Use ServicesGrid for menu dropdown, with mainLinks column */}
            <ServicesGrid
              services={(LINKS.services.children || []) as LinkType[]}
              mainLinks={mainLinks}
              fadeUp={true}
              isMenuOpen={isMenuOpen}
              showMainLinksColumn={true}
            />
          </div>
        </div>
      </header>
      {/* Overlay for blur when dropdown or menu is open, behind header */}
      {(isNavHovered || isMenuOpen) && (
        <div
          className="fixed inset-0 z-40 bg-black/10 backdrop-blur-sm border-b border-b-slate-200 transition-all duration-300"
          style={{ pointerEvents: "auto" }}
          onClick={() => {
            setIsNavHovered(false)
            setIsMenuOpen(false)
          }}
        />
      )}
    </>
  )
}
